import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons';
import AppShell from './components/layout/AppShell';
import ProtectedRoute from './components/auth/ProtectedRoute';
import OwnerRoute from './components/auth/OwnerRoute';
import { ErrorProvider } from './contexts/ErrorContext';
import { ComposedUserProvider } from './contexts/user/ComposedUserProvider';
import { LayoutProvider } from './contexts/LayoutContext';
import { BacktestProvider } from './contexts/BacktestContext';
import { OptimizationResultSyncer } from './components/optimization/OptimizationResultSyncer';
import { BacktestResultProvider } from './contexts/BacktestResultContext';
import { WFAQueueProvider } from './contexts/WFAQueueContext';
import { OptimizationNotifications } from './components/optimization/OptimizationNotifications';
import { QueryProvider } from './providers/QueryProvider';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-6 right-6 z-50 w-10 h-10 rounded-full bg-[#9d4edd] hover:bg-[#8b3ec7] text-white shadow-lg shadow-black/30 flex items-center justify-center transition-all duration-200 opacity-80 hover:opacity-100"
      aria-label="Scroll to top"
    >
      <FontAwesomeIcon icon={faChevronUp} className="text-sm" />
    </button>
  );
}
import { useStore } from './store';

// Lazy load pages for better performance
const LandingPage = lazy(() => import('./pages/LandingPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const LoginPage = lazy(() => import('./components/auth/LoginPage'));
const SavedWFAListPage = lazy(() => import('./components/pages/SavedWFAListPage'));
const SavedWFADetailsPage = lazy(() => import('./components/pages/SavedWFADetailsPage'));
const WFAResultPageV2 = lazy(() => import('./components/pages/WFAResultPageV2'));
const ProfilePage = lazy(() => import('./components/pages/ProfilePage'));
const BillingPage = lazy(() => import('./components/pages/BillingPage'));

const AccountsPage = lazy(() => import('./components/pages/AccountsPage'));
const AdminArticleListPage = lazy(() => import('./pages/admin/AdminArticleListPage'));
const AdminArticleEditorPage = lazy(() => import('./pages/admin/AdminArticleEditorPage'));
const SupportPage = lazy(() => import('./components/pages/SupportPage'));
const DocumentationPage = lazy(() => import('./pages/DocumentationPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const RiskDisclosurePage = lazy(() => import('./pages/RiskDisclosurePage'));
const SignupPage = lazy(() => import('./components/auth/SignupPage'));
const ForgotPasswordPage = lazy(() => import('./components/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./components/auth/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./components/auth/VerifyEmailPage'));
const StrategyReportViewPage = lazy(() => import('./pages/StrategyReportViewPage'));
const StrategyIDEPage = lazy(() => import('./components/strategy-ide/StrategyIDEPage'));
const ResearchLabPage = lazy(() => import('./components/research-lab/ResearchLabPage').then(m => ({ default: m.ResearchLabPage })));
const NewProjectFlow = lazy(() => import('./components/research-lab/NewProjectFlow'));
const WorkspacePage = lazy(() => import('./pages/features/WorkspacePage'));
const ValidationPage = lazy(() => import('./pages/features/ValidationPage'));
const OptimizationPage = lazy(() => import('./pages/features/OptimizationPage'));
const ContinuousValidationPage = lazy(() => import('./pages/features/ContinuousValidationPage'));
const ResearchPipelinePage = lazy(() => import('./pages/features/ResearchPipelinePage'));
const LabsIndexPage = lazy(() => import('./pages/labs/LabsIndexPage'));
const TdrIndicatorPage = lazy(() => import('./pages/labs/TdrIndicatorPage'));
const StrategyCalculatorPage = lazy(() => import('./pages/labs/StrategyCalculatorPage'));
const PositionSizeCalculatorPage = lazy(() => import('./pages/labs/PositionSizeCalculatorPage'));
const CommunityPage = lazy(() => import('./pages/CommunityPage'));
const StrategyFailureAdPage = lazy(() => import('./pages/StrategyFailureAdPage'));
const LearnHubPage = lazy(() => import('./pages/learn/LearnHubPage'));
const LearnCategoryPage = lazy(() => import('./pages/learn/LearnCategoryPage'));
const LearnArticlePage = lazy(() => import('./pages/learn/LearnArticlePage'));
const ResearchNotesHubPage = lazy(() => import('./pages/research/ResearchNotesHubPage'));
const ResearchArticlePage = lazy(() => import('./pages/research/ResearchArticlePage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

function App() {
  const { refreshUserData } = useStore();

  // Initialize user data on app load if user is authenticated
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      // User is logged in, refresh their profile data
      refreshUserData().catch(() => {
        // Silently fail - user will need to re-login if token is invalid
      });
    }
  }, [refreshUserData]);

  return (
    <QueryProvider>
      <ErrorProvider>
        <ComposedUserProvider>
          <LayoutProvider>
            <BacktestProvider>
              {/* WFA Queue Provider - connects only when needed (active jobs) */}
              <WFAQueueProvider autoConnect={false} pollInterval={10000}>
                <ScrollToTop />
                <ScrollToTopButton />
                {/* Sync optimization queue results to BacktestContext */}
                <OptimizationResultSyncer />
                <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                  <Suspense fallback={
                    <div className="flex items-center justify-center min-h-screen">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100"></div>
                    </div>
                  }>
                    <Routes>
                      {/* Landing Page - Public */}
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/register" element={<Navigate to="/signup" replace />} />
                      <Route path="/beta" element={<Navigate to="/signup" replace />} />
                      <Route path="/pricing" element={<PricingPage />} />
                      <Route path="/help" element={<Navigate to="/docs" replace />} />
                      <Route path="/help/:articleId" element={<Navigate to="/docs" replace />} />
                      <Route path="/docs" element={<DocumentationPage />} />
                      <Route path="/docs/*" element={<DocumentationPage />} />
                      <Route path="/blog" element={<BlogPage />} />
                      <Route path="/blog/:postId" element={<Navigate to="/blog" replace />} />
                      <Route path="/terms" element={<TermsOfServicePage />} />
                      <Route path="/privacy" element={<PrivacyPolicyPage />} />
                      <Route path="/cookies" element={<CookiePolicyPage />} />
                      <Route path="/risk-disclosure" element={<RiskDisclosurePage />} />
                      <Route path="/features/workspace" element={<WorkspacePage />} />
                      <Route path="/features/validation" element={<ValidationPage />} />
                      <Route path="/features/optimization" element={<OptimizationPage />} />
                      <Route path="/features/continuous-validation" element={<ContinuousValidationPage />} />
                      <Route path="/features/research-pipeline" element={<ResearchPipelinePage />} />
                      <Route path="/ads/why-most-strategies-fail" element={<StrategyFailureAdPage />} />

                      {/* Labs - Lead magnets and free tools */}
                      <Route path="/labs" element={<LabsIndexPage />} />
                      <Route path="/labs/tradingview-indicator-tdr" element={<TdrIndicatorPage />} />
                      <Route path="/labs/tdr" element={<Navigate to="/labs/tradingview-indicator-tdr" replace />} />
                      <Route path="/labs/strategy-performance-calculator" element={<StrategyCalculatorPage />} />
                      <Route path="/labs/position-size-calculator" element={<PositionSizeCalculatorPage />} />

                      {/* Community */}
                      <Route path="/community" element={<CommunityPage />} />

                      {/* Learn */}
                      <Route path="/learn" element={<LearnHubPage />} />
                      <Route path="/learn/:category" element={<LearnCategoryPage />} />
                      <Route path="/learn/:category/:slug" element={<LearnArticlePage />} />

                      {/* Research Notes */}
                      <Route path="/research" element={<ResearchNotesHubPage />} />
                      <Route path="/research/:slug" element={<ResearchArticlePage />} />

                      {/* Auth routes */}
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/signup" element={<SignupPage />} />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                      <Route path="/reset-password" element={<ResetPasswordPage />} />
                      <Route path="/verify-email" element={<VerifyEmailPage />} />

                      {/* Protected routes with new App Shell navigation */}
                      <Route
                        path="/app/*"
                        element={
                          <ProtectedRoute>
                            <BacktestResultProvider>
                              <AppShell />
                            </BacktestResultProvider>
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<ResearchLabPage />} />
                        <Route path="dashboard" element={<Navigate to="/app" replace />} />
                        <Route path="backtest-studio" element={<Navigate to="/app" replace />} />
                        <Route path="backtests" element={<Navigate to="/app" replace />} />
                        <Route path="backtests/history" element={<Navigate to="/app" replace />} />

                        <Route path="strategies/code" element={<StrategyIDEPage />} />
                        <Route path="lab" element={<Navigate to="/app" replace />} />
                        <Route path="lab/new" element={<NewProjectFlow />} />
                        <Route path="lab/project/:projectId" element={<ResearchLabPage />} />
                        <Route path="wfa/saved" element={<SavedWFAListPage />} />
                        <Route path="wfa/saved/:id" element={<SavedWFADetailsPage />} />
                        <Route path="wfa/result/:id" element={<WFAResultPageV2 />} />
                        <Route path="adaptive-flow/:wfaId" element={<StrategyReportViewPage />} />
                        <Route path="profile" element={<ProfilePage />} />
                        <Route path="support" element={<SupportPage />} />
                        <Route path="billing" element={<BillingPage />} />
                        <Route path="subscription" element={<Navigate to="/app/billing" replace />} />
                        <Route path="invoices" element={<Navigate to="/app/billing" replace />} />
                        <Route path="admin/accounts" element={
                          <OwnerRoute>
                            <AccountsPage />
                          </OwnerRoute>
                        } />
                        <Route path="admin/articles" element={
                          <OwnerRoute>
                            <AdminArticleListPage />
                          </OwnerRoute>
                        } />
                        <Route path="admin/articles/:slug" element={
                          <OwnerRoute>
                            <AdminArticleEditorPage />
                          </OwnerRoute>
                        } />
                      </Route>

                      {/* 404 catch-all */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>

                  {/* Global optimization notifications */}
                  <OptimizationNotifications
                    position="top-right"
                    maxNotifications={5}
                    defaultDuration={5000}
                  />
                </div>
              </WFAQueueProvider>
            </BacktestProvider>
          </LayoutProvider>
        </ComposedUserProvider>
      </ErrorProvider>
    </QueryProvider>
  );
}

export default App;
